
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, RefreshCw, X, AlertTriangle, Upload } from "lucide-react";
import { useAiArtGeneration } from "@/hooks/use-ai-art-generation";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { supabase } from "@/integrations/supabase/client";

interface AiArtGeneratorProps {
  onImageGenerated: (url: string) => void;
  onSetCategory: (category: string) => void;
}

export function AiArtGenerator({ onImageGenerated, onSetCategory }: AiArtGeneratorProps) {
  const [prompt, setPrompt] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [referenceImage, setReferenceImage] = useState<string | null>(null);
  const [isUploadingRef, setIsUploadingRef] = useState(false);
  
  const { isGenerating, error, generatedImageUrl, generateImage, reset } = useAiArtGeneration();

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Por favor, forneça uma descrição para gerar a imagem");
      return;
    }

    if (!apiKey.trim()) {
      toast.error("É necessário fornecer uma chave de API da OpenAI");
      return;
    }

    try {
      const imageUrl = await generateImage(prompt, apiKey, referenceImage || undefined);
      if (imageUrl) {
        onImageGenerated(imageUrl);
        onSetCategory("ai-art"); // Set category to "ai-art" when using AI
      }
    } catch (e) {
      console.error("Error in handleGenerate:", e);
    }
  };

  const handleDeleteImage = () => {
    reset();
    onImageGenerated("");
  };

  const handleRegenerateImage = () => {
    handleGenerate();
  };

  const toggleShowApiKey = () => {
    setShowApiKey(!showApiKey);
  };

  const handleReferenceImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      setIsUploadingRef(true);
      
      // Upload the reference image to Supabase storage
      const fileExt = file.name.split('.').pop();
      const filePath = `reference/${Math.random().toString(36).substring(2)}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('pins')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });
        
      if (uploadError) {
        throw uploadError;
      }
      
      const { data } = supabase.storage
        .from('pins')
        .getPublicUrl(filePath);
        
      setReferenceImage(data.publicUrl);
      toast.success("Imagem de referência enviada");
    } catch (error) {
      console.error("Error uploading reference image:", error);
      toast.error("Erro ao enviar imagem de referência");
    } finally {
      setIsUploadingRef(false);
    }
  };

  const handleRemoveReferenceImage = () => {
    setReferenceImage(null);
  };

  return (
    <div className="space-y-4 p-4 border rounded-lg">
      {!generatedImageUrl ? (
        <>
          <div className="space-y-2">
            <Label htmlFor="prompt">Descrição da imagem</Label>
            <Textarea
              id="prompt"
              placeholder="Descreva a imagem que você deseja gerar. Ex: Uma paisagem futurista com arranha-céus e carros voadores."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={isGenerating}
              className="h-24"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="referenceImage">Imagem de referência (opcional)</Label>
            {!referenceImage ? (
              <div className="relative">
                <Input 
                  id="referenceImage" 
                  type="file" 
                  accept="image/*"
                  disabled={isGenerating || isUploadingRef}
                  onChange={handleReferenceImageUpload}
                  className="hidden"
                />
                <div 
                  onClick={() => document.getElementById("referenceImage")?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-md p-4 flex flex-col items-center justify-center h-32 cursor-pointer hover:bg-gray-50"
                >
                  {isUploadingRef ? (
                    <Loader2 className="h-10 w-10 text-gray-400 animate-spin" />
                  ) : (
                    <>
                      <Upload className="h-10 w-10 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500">Clique para enviar uma imagem de referência</p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="relative border rounded-md overflow-hidden">
                <AspectRatio ratio={16/9}>
                  <img 
                    src={referenceImage} 
                    alt="Imagem de referência" 
                    className="w-full h-full object-cover"
                  />
                </AspectRatio>
                <Button
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={handleRemoveReferenceImage}
                  disabled={isGenerating}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
            {referenceImage && (
              <p className="text-xs text-muted-foreground">
                A IA usará esta imagem como referência para criar uma variação baseada na sua descrição.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="apiKey">Chave da API OpenAI</Label>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={toggleShowApiKey}
                type="button"
              >
                {showApiKey ? "Ocultar" : "Mostrar"}
              </Button>
            </div>
            <Input
              id="apiKey"
              type={showApiKey ? "text" : "password"}
              placeholder="sk-..."
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              disabled={isGenerating}
            />
            <p className="text-xs text-muted-foreground">
              Sua chave será usada apenas para gerar esta imagem e não será armazenada.
              <a 
                href="https://platform.openai.com/api-keys" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline ml-1"
              >
                Obter uma chave API
              </a>
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="text-sm">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {error}
                {error.includes("limite de crédito") && (
                  <div className="mt-2">
                    <a 
                      href="https://platform.openai.com/usage" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm underline hover:no-underline"
                    >
                      Verificar saldo da conta OpenAI
                    </a>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          <Button 
            onClick={handleGenerate} 
            disabled={isGenerating || !prompt.trim() || !apiKey.trim()} 
            className="w-full"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Gerando...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" /> Gerar arte com IA
              </>
            )}
          </Button>
        </>
      ) : (
        <div className="space-y-4">
          <div className="border rounded-lg overflow-hidden">
            <AspectRatio ratio={1}>
              <img
                src={generatedImageUrl}
                alt="Arte gerada por IA"
                className="w-full h-full object-cover"
              />
            </AspectRatio>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="destructive" 
              onClick={handleDeleteImage} 
              className="flex-1"
            >
              <X className="h-4 w-4 mr-2" /> Excluir
            </Button>
            <Button 
              variant="default" 
              onClick={handleRegenerateImage} 
              disabled={isGenerating}
              className="flex-1"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isGenerating ? 'animate-spin' : ''}`} /> 
              {isGenerating ? "Gerando..." : "Gerar outra"}
            </Button>
          </div>
          
          <div className="border border-dashed p-3 rounded-lg bg-muted/50">
            <p className="text-sm font-medium">Descrição utilizada:</p>
            <p className="text-sm text-muted-foreground">{prompt}</p>
          </div>
        </div>
      )}
    </div>
  );
}
